import type { QueryClient } from '@tanstack/react-query'
import {
  Outlet,
  createRootRouteWithContext,
  lazyRouteComponent,
  createRoute,
  createRouter,
  redirect,
  useParams,
} from '@tanstack/react-router'
import { z } from 'zod'

import { meQuery } from './api/queries.js'
import { NotFound } from './components/NotFound.js'
import { LoginPage } from './features/auth/LoginPage.js'
import { CentralPage, SsoPage } from './features/auth/SsoPage.js'
import { CONTENT_RESOURCES, type ContentResourceKey } from './api/resources.js'
import { ORDER_STATUSES } from '@bak/types'

// Everything except sign-in, the shell and the dashboard loads on demand: the editor (block
// forms, rich text), settings and the rest are only downloaded when their screen is opened, and
// `defaultPreload: 'intent'` fetches them as soon as a link is hovered.
const AdminLayout = lazyRouteComponent(() => import('./components/AdminLayout.js'), 'AdminLayout')
const DashboardPage = lazyRouteComponent(() => import('./features/dashboard/DashboardPage.js'), 'DashboardPage')
const ContentListPage = lazyRouteComponent(() => import('./features/content/ContentListPage.js'), 'ContentListPage')
const ContentEditorPage = lazyRouteComponent(() => import('./features/content/ContentEditorPage.js'), 'ContentEditorPage')
const InquiriesPage = lazyRouteComponent(() => import('./features/leads/InquiriesPage.js'), 'InquiriesPage')
const MailboxPage = lazyRouteComponent(() => import('./features/mailbox/MailboxPage.js'), 'MailboxPage')
const MailAddressesPage = lazyRouteComponent(() => import('./features/mailbox/MailAddressesPage.js'), 'MailAddressesPage')
const LiveChatPage = lazyRouteComponent(() => import('./features/livechat/LiveChatPage.js'), 'LiveChatPage')
const LiveChatRoomPage = lazyRouteComponent(() => import('./features/livechat/LiveChatRoomPage.js'), 'LiveChatRoomPage')
const DigicsAnswersPage = lazyRouteComponent(() => import('./features/livechat/DigicsAnswersPage.js'), 'DigicsAnswersPage')
const DigicsSettingsPage = lazyRouteComponent(() => import('./features/livechat/DigicsSettingsPage.js'), 'DigicsSettingsPage')
const ProfilePage = lazyRouteComponent(() => import('./features/auth/ProfilePage.js'), 'ProfilePage')
const MediaLibraryPage = lazyRouteComponent(() => import('./features/media/MediaLibraryPage.js'), 'MediaLibraryPage')
const NavigationPage = lazyRouteComponent(() => import('./features/site/NavigationPage.js'), 'NavigationPage')
const SpecReferencePage = lazyRouteComponent(() => import('./features/site/SpecReferencePage.js'), 'SpecReferencePage')
const SettingsPage = lazyRouteComponent(() => import('./features/site/SettingsPage.js'), 'SettingsPage')
const RolesPage = lazyRouteComponent(() => import('./features/users/RolesPage.js'), 'RolesPage')
const ContentTypesPage = lazyRouteComponent(() => import('./features/site/ContentTypesPage.js'), 'ContentTypesPage')
const LocationsPage = lazyRouteComponent(() => import('./features/site/LocationsPage.js'), 'LocationsPage')
const SectionListPage = lazyRouteComponent(() => import('./features/sections/SectionListPage.js'), 'SectionListPage')
const SectionEditorPage = lazyRouteComponent(() => import('./features/sections/SectionEditorPage.js'), 'SectionEditorPage')
const PlanPage = lazyRouteComponent(() => import('./features/site/PlanPage.js'), 'PlanPage')
const DomainsPage = lazyRouteComponent(() => import('./features/site/DomainsPage.js'), 'DomainsPage')
const SubscriptionPage = lazyRouteComponent(() => import('./features/site/SubscriptionPage.js'), 'SubscriptionPage')
const UsersPage = lazyRouteComponent(() => import('./features/users/UsersPage.js'), 'UsersPage')
const OrdersPage = lazyRouteComponent(() => import('./features/shop/OrdersPage.js'), 'OrdersPage')
const OrderDetailPage = lazyRouteComponent(() => import('./features/shop/OrderDetailPage.js'), 'OrderDetailPage')
const CustomersPage = lazyRouteComponent(() => import('./features/shop/CustomersPage.js'), 'CustomersPage')
const PromosPage = lazyRouteComponent(() => import('./features/shop/PromosPage.js'), 'PromosPage')
const ReportsPage = lazyRouteComponent(() => import('./features/shop/ReportsPage.js'), 'ReportsPage')
const LowStockPage = lazyRouteComponent(() => import('./features/shop/LowStockPage.js'), 'LowStockPage')
const PaymentsPage = lazyRouteComponent(() => import('./features/shop/PaymentsPage.js'), 'PaymentsPage')

/** Shown while a screen's code is downloading (only after 150 ms, so fast loads never flash it). */
function ScreenSkeleton() {
  return <div className="mv-skeleton h-96 rounded-[var(--mv-radius-lg)]" />
}

type RouterContext = { queryClient: QueryClient }

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: LoginPage,
})

const ssoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sso',
  validateSearch: z.object({ token: z.string().optional() }),
  component: SsoPage,
})

/** The handoff from admin.digimasi.com (#228 step 3): the token is in the fragment. */
const centralRoute = createRoute({ getParentRoute: () => rootRoute, path: '/central', component: CentralPage })

/** Authenticated layout. The API is the real gate; this only decides what to render. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery)
    } catch {
      throw redirect({ to: '/login', search: { redirect: location.pathname } })
    }
  },
  component: AdminLayout,
})

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage })

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['draft', 'published', 'archived']).optional(),
  section: z.enum(['journal', 'news']).optional(),
  kind: z.enum(['material', 'finish']).optional(),
  page: z.coerce.number().int().min(1).optional(),
  // Articles: order by publish date (default) or creation date.
  sort: z.enum(['published', 'created']).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
})
export type ListSearch = z.infer<typeof listSearch>

// Journal and News each get their own list in the menu; both are the articles resource, fixed to one section.
const articleSectionRoutes = (['journal', 'news'] as const).map((section) =>
  createRoute({ getParentRoute: () => appRoute, path: `/${section}`, validateSearch: listSearch, component: () => <ContentListPage resource="articles" section={section} /> }),
)

const contentRoutes = (Object.keys(CONTENT_RESOURCES) as ContentResourceKey[]).flatMap((key) => [
  createRoute({
    getParentRoute: () => appRoute,
    path: `/${key}`,
    validateSearch: listSearch,
    component: () => <ContentListPage resource={key} />,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: `/${key}/new`,
    validateSearch: z.object({ section: z.enum(['journal', 'news']).optional() }),
    component: () => <ContentEditorPage resource={key} id={null} />,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: `/${key}/$id`,
    component: function EditRoute() {
      const { id } = contentEditParams(key)
      return <ContentEditorPage resource={key} id={id} />
    },
  }),
])

// TanStack's typed useParams needs the route object; a loose read keeps the generated routes simple.
function contentEditParams(_key: ContentResourceKey): { id: string } {
  const params = useParams({ strict: false }) as { id?: string }
  return { id: params.id ?? '' }
}

// Sections the site owner added: /c/<section key>/{items|categories}[/new|/<id>]
const sectionRoutes = (['items', 'categories'] as const).flatMap((segment) => {
  const kind = segment === 'items' ? ('item' as const) : ('category' as const)
  const typeKey = () => (useParams({ strict: false }) as { typeKey?: string }).typeKey ?? ''
  const entryId = () => (useParams({ strict: false }) as { id?: string }).id ?? ''
  return [
    createRoute({ getParentRoute: () => appRoute, path: `/c/$typeKey/${segment}`, component: function List() { return <SectionListPage typeKey={typeKey()} kind={kind} /> } }),
    createRoute({ getParentRoute: () => appRoute, path: `/c/$typeKey/${segment}/new`, component: function New() { return <SectionEditorPage typeKey={typeKey()} kind={kind} id={null} /> } }),
    createRoute({ getParentRoute: () => appRoute, path: `/c/$typeKey/${segment}/$id`, component: function Edit() { return <SectionEditorPage typeKey={typeKey()} kind={kind} id={entryId()} /> } }),
  ]
})

const mediaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/media',
  validateSearch: z.object({ q: z.string().optional(), page: z.coerce.number().int().min(1).optional() }),
  component: MediaLibraryPage,
})

const inquiriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inquiries',
  validateSearch: z.object({
    q: z.string().optional(),
    status: z.enum(['new', 'contacted', 'qualified', 'won', 'lost', 'spam']).optional(),
    page: z.coerce.number().int().min(1).optional(),
  }),
  component: InquiriesPage,
})

const mailboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/inbox',
  validateSearch: z.object({
    q: z.string().optional(),
    recipient: z.string().optional(),
    unread: z.boolean().optional().catch(undefined),
    sent: z.boolean().optional().catch(undefined),
    catchAll: z.boolean().optional().catch(undefined),
    page: z.coerce.number().int().min(1).optional(),
  }),
  component: MailboxPage,
})
const mailAddressesRoute = createRoute({ getParentRoute: () => appRoute, path: '/inbox/addresses', component: MailAddressesPage })

// The website chat's staff inbox (#284 B3): only where the API has the chat (GET /livechat/status).
const liveChatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/live-chat',
  validateSearch: z.object({ tab: z.enum(['needs', 'open', 'closed']).optional().catch(undefined) }),
  component: LiveChatPage,
})
const liveChatRoomRoute = createRoute({ getParentRoute: () => appRoute, path: '/live-chat/$id', component: LiveChatRoomPage })
// DigiCS's prepared answers and settings (#284 B4).
const digicsAnswersRoute = createRoute({ getParentRoute: () => appRoute, path: '/live-chat/answers', component: DigicsAnswersPage })
const digicsSettingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/live-chat/settings', component: DigicsSettingsPage })

// Online store (flag `store`): orders and customers.
const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/orders',
  validateSearch: z.object({
    status: z.enum(ORDER_STATUSES).optional().catch(undefined),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
    q: z.string().max(200).optional(),
    page: z.coerce.number().int().min(1).optional(),
  }),
  component: OrdersPage,
})
const orderDetailRoute = createRoute({ getParentRoute: () => appRoute, path: '/orders/$id', component: OrderDetailPage })
const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/customers',
  validateSearch: z.object({ q: z.string().max(200).optional(), page: z.coerce.number().int().min(1).optional() }),
  component: CustomersPage,
})

const promosRoute = createRoute({ getParentRoute: () => appRoute, path: '/promos', component: PromosPage })
const reportsRoute = createRoute({ getParentRoute: () => appRoute, path: '/reports', component: ReportsPage })
const lowStockRoute = createRoute({ getParentRoute: () => appRoute, path: '/low-stock', component: LowStockPage })
const paymentsRoute = createRoute({ getParentRoute: () => appRoute, path: '/payments', component: PaymentsPage })

const navigationRoute = createRoute({ getParentRoute: () => appRoute, path: '/navigation', component: NavigationPage })
const specReferenceRoute = createRoute({ getParentRoute: () => appRoute, path: '/spec-reference', component: SpecReferencePage })
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsPage })
const contentSettingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/content-settings', component: () => <ContentTypesPage scope="content" /> })
const catalogueSettingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/catalogue-settings', component: () => <ContentTypesPage scope="catalogue" /> })
// The old combined screen; bookmarks land on the catalogue half.
const contentTypesRoute = createRoute({ getParentRoute: () => appRoute, path: '/content-types', beforeLoad: () => { throw redirect({ to: '/catalogue-settings' }) } })
const rolesRoute = createRoute({ getParentRoute: () => appRoute, path: '/roles', component: RolesPage })
const locationsRoute = createRoute({ getParentRoute: () => appRoute, path: '/locations', component: LocationsPage })
const planRoute = createRoute({ getParentRoute: () => appRoute, path: '/plan', component: PlanPage })
const domainsRoute = createRoute({ getParentRoute: () => appRoute, path: '/domains', component: DomainsPage })
const subscriptionRoute = createRoute({ getParentRoute: () => appRoute, path: '/subscription', component: SubscriptionPage })
const usersRoute = createRoute({ getParentRoute: () => appRoute, path: '/users', component: UsersPage })
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: '/profile', component: ProfilePage })

const routeTree = rootRoute.addChildren([
  loginRoute,
  ssoRoute,
  centralRoute,
  appRoute.addChildren([
    dashboardRoute,
    ...contentRoutes,
    ...articleSectionRoutes,
    ...sectionRoutes,
    mediaRoute,
    inquiriesRoute,
    mailboxRoute,
    mailAddressesRoute,
    liveChatRoute,
    liveChatRoomRoute,
    digicsAnswersRoute,
    digicsSettingsRoute,
    ordersRoute,
    orderDetailRoute,
    customersRoute,
    promosRoute,
    paymentsRoute,
    reportsRoute,
    lowStockRoute,
    navigationRoute,
    specReferenceRoute,
    settingsRoute,
    planRoute,
    subscriptionRoute,
    domainsRoute,
    locationsRoute,
    contentSettingsRoute,
    catalogueSettingsRoute,
    contentTypesRoute,
    rolesRoute,
    usersRoute,
    profileRoute,
  ]),
])

export const createAppRouter = (queryClient: QueryClient) =>
  createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', scrollRestoration: true, defaultPendingMs: 150, defaultPendingComponent: ScreenSkeleton })

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
